import { Component, inject, resource, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BbitString, IBbitPlaceBookingCompanion, IBbitSelectDataRow } from '@bbitgmbh/bbit.utils';
import { BbitWebsiteService, I18nPipe } from '@bbitgmbh/website.sdk';
import { NgIcon } from '@ng-icons/core';
import { LoadingComponent } from '../../../helper/loading/loading.component';
import { EditCompanionOverlayRef } from './edit-companion.overlay-ref';

@Component({
  selector: 'app-edit-companion',
  imports: [ReactiveFormsModule, LoadingComponent, NgIcon, I18nPipe],
  templateUrl: './edit-companion.component.html',
  styleUrl: './edit-companion.component.scss',
})
export class EditCompanionComponent {
  private _overlayRef = inject(EditCompanionOverlayRef);
  companionForm = new FormGroup({
    _id: new FormControl(''),
    name: new FormGroup({
      salutation: new FormControl('', [Validators.required]),
      givenName: new FormControl('', [Validators.required]),
      familyName: new FormControl('', [Validators.required]),
    }),
    address: new FormGroup({
      street: new FormControl('', [Validators.required]),
      locality: new FormControl('', [Validators.required]),
      postalCode: new FormControl('', [Validators.required]),
    }),
    mobile: new FormControl('', []),
    eMail: new FormControl('', [Validators.email]),
  });
  private _website = inject(BbitWebsiteService);
  public id = signal<string | undefined>('');
  public salutationRows: IBbitSelectDataRow[] = [
    { _id: '#male', displayValue: 'Herr' },
    { _id: '#female', displayValue: 'Frau' },
  ];
  public companion = resource<IBbitPlaceBookingCompanion, { _id: string | undefined }>({
    params: () => ({ _id: this.id() }),
    loader: async ({ params }) => {
      if (!params._id) {
        this.companionForm.patchValue({});
        return {};
      }
      const response = await fetch(
        `${this._website.getBlApiUrl()}/placeBooking/companions/${BbitString.stripBeginningHashtag(params._id)}`,
        {
          method: 'GET',
          headers: {
            Authorization: `${this._website.getAccessToken()}`,
          },
        },
      );
      const data = await response.json();
      if (data.companion) {
        this.companionForm.patchValue(data.companion);
      }
      return data.companion;
    },
  });

  constructor() {
    this.id.set(this._overlayRef.getId());
  }

  async save(): Promise<void> {
    const body = this.companionForm.value;
    const response = await fetch(`${this._website.getBlApiUrl()}/placeBooking/companions`, {
      method: 'POST',
      headers: {
        Authorization: `${this._website.getAccessToken()}`,
      },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    this.companionForm.reset();
    this.companionForm.patchValue(data.companion);
    this._overlayRef.close(true);
  }

  async remove(): Promise<void> {
    const body = this.companionForm.value;
    const response = await fetch(
      `${this._website.getBlApiUrl()}/placeBooking/companions/${BbitString.stripBeginningHashtag(body._id || '')}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `${this._website.getAccessToken()}`,
        },
      },
    );
    await response.json();

    if (!response.ok) {
      throw new Error("Couldn't delete companion");
    }
    this._overlayRef.close(true);
  }

  close(): void {
    this._overlayRef.close(false);
  }
}
